// frontend/src/shared/hooks/useTenant.ts
// Business-level tenant type (COMPANY vs INTERIM_AGENCY) is HR-specific.
// Admin module TenantType (CENTRAL_AUTHORITY, MEMBER_SELF_MANAGED, etc.) is platform-level.
// This hook provides the business context for conditional UI rendering.
import { useCurrentUser } from '@damar/admin-hooks';
import type { CurrentUserResponse } from '@damar/admin-hooks';
import { useAuth } from './useAuth';

export type BusinessTenantType = 'COMPANY' | 'INTERIM_AGENCY';

/**
 * Determines the business tenant type from the current user's tenant.
 * Uses `interim` module availability as a proxy: if the tenant's licence includes
 * the `interim` module, it's an interim agency. When the backend exposes a
 * `business_type` field on Tenant, switch to that instead.
 */
export const useBusinessTenantType = (): BusinessTenantType => {
  const { permissions, isAdminRole } = useAuth();
  // Admins / dev users see every persona — keeps nav usable in dev mode
  // (where the backend doesn't expose explicit module flags) and for org admins.
  if (isAdminRole) return 'INTERIM_AGENCY';
  if (!permissions?.actions) return 'COMPANY';
  const hasInterimModule = permissions.actions.some(
    (a) => a.module === 'interim' && a.hasAccess
  );
  // Fallback : presence of any HR mission/interim permission also implies an
  // interim agency tenant (covers the case where the licence module flag isn't
  // surfaced but the role grants interim-related features).
  if (hasInterimModule) return 'INTERIM_AGENCY';
  const hasInterimFeature = permissions.actions.some(
    (a) => a.hasAccess && (a.feature.includes('mission') || a.feature.includes('interim') || a.feature.includes('client'))
  );
  return hasInterimFeature ? 'INTERIM_AGENCY' : 'COMPANY';
};

export const useIsInterim = (): boolean => {
  return useBusinessTenantType() === 'INTERIM_AGENCY';
};

export const useTenantId = (): string | null => {
  const { data: user } = useCurrentUser();
  const currentUser = user as unknown as CurrentUserResponse | undefined;
  return currentUser?.userInfo?.tenantId ?? null;
};

export default useBusinessTenantType;
